import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Avatar,
  AvatarFallback,
  Badge,
  Button,
  Heading,
  Icon,
  Tooltip,
  TooltipContent,
  useToast,
} from "@elaniintc/goes-design-system";
import { Question as QuestionComponent } from "./components/question";
import { GripHandle } from "@/components/ui/grip-handle";
import { TooltipTrigger } from "@/components/ui/tooltip";
import FaqModal from "./components/faq-modal";
import CreateDropdown from "./components/create-dropdown";
import { SectionModal } from "./components/new-section-modal";
import SectionOptions from "./components/section-options";
import { SortDropdown } from "./components/sort-dropdown";
import { useHeaderButton } from "@/contexts/header-button-context";
import {
  FAQConfirmationModal,
  type ConfirmationModalMode,
} from "./components/confirmation-modal";
import {
  useListFaqCategories,
  useListFaqs,
  useRefreshFaqs,
} from "@/queries/faq/faq.api";
import { QuestionManager } from "./components/question-manager";
import { useNavigate } from "react-router";
import { DataManager, type DataManagerResult } from "./components/data-manager";
import { FAQEmptyState } from "./components/empty-state";
import { useQueryClient } from "@tanstack/react-query";
import { useAuthorization } from "@/hooks/use-authorization";
import { Permissions } from "@/utils/permissions";
import { Skeleton } from "@/components/ui/skeleton";
import { FaqsSkeleton } from "./components/faqs-skeleton";
import dayjs from "dayjs";

interface Question {
  id: string;
  question: string;
  answer: string;
  category: string;
}

interface Section {
  section: string;
  questions: Question[];
}

interface SortableQuestionProps {
  id: string;
  question: string;
  onEdit: () => void;
  canEdit?: boolean;
}

function SortableQuestion({
  id,
  question,
  canEdit,
  onEdit,
}: SortableQuestionProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
  } = useSortable({ id });
  const style = { transform: CSS.Translate.toString(transform), transition };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex group space-x-2 items-center -translate-x-7"
    >
      {canEdit ? (
        <div
          ref={setActivatorNodeRef}
          {...listeners}
          {...attributes}
          className="cursor-grab"
        >
          <GripHandle className="text-transparent group-hover:text-on-disabled-quiet" />
        </div>
      ) : (
        <div className="w-[18px] h-px"></div>
      )}

      <QuestionComponent question={question} />

      {canEdit && (
        <Button
          size="icon"
          variant="ghost"
          className="text-transparent group-hover:text-body-loud"
          aria-label="Editar pregunta"
          onClick={onEdit}
        >
          <Icon type="far" name="edit" />
        </Button>
      )}
    </div>
  );
}

export const FaqsDetail: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuthorization();
  const canRead = hasPermission(Permissions.faqs.read);
  const canCreate = hasPermission(Permissions.faqs.create);
  const canEdit = hasPermission(Permissions.faqs.edit);
  const canDelete = hasPermission(Permissions.faqs.delete);

  const { data: listFaqs, isLoading } = useListFaqs(canRead);

  const idCounter = useRef(0);
  const getNextId = () => (++idCounter.current).toString();

  const { data: categories } = useListFaqCategories(canRead);

  const [secciones, setSecciones] = useState<Section[]>(
    QuestionManager(listFaqs),
  );

  useEffect(() => {
    setSecciones(QuestionManager(listFaqs, categories));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listFaqs]);

  const [showCrudSectionModal, setShowCrudSectionModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [sectionMode, setSectionMode] = useState<"create" | "edit">("create");
  const [sectionToEdit, setSectionToEdit] = useState<string>("");
  const [editingFaq, setEditingFaq] = useState<{
    section: string;
    index: number;
    data: Question;
  } | null>(null);
  const [createCategory, setCreateCategory] = useState<string>("General");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [confirmationState, setConfirmationState] = useState<
    ConfirmationModalMode | "off" | "delete-section"
  >("off");
  const [sectionToDelete, setSectionToDelete] = useState<string>("");

  const { setComp } = useHeaderButton();
  const { showToast } = useToast();

  const faqsStatus = listFaqs?.data?.[0]
    ? listFaqs?.data?.[0]?.status
    : "draft";

  // Header buttons for save, publish, cancel
  useEffect(() => {
    setComp(
      <>
        {hasUnsavedChanges && (
          <Button
            variant="quiet"
            size="sm"
            aria-label="Cancelar edición"
            onClick={() => setConfirmationState("cancel")}
          >
            Cancelar <Icon type="far" name="xmark" />
          </Button>
        )}
        {(canCreate || canEdit) && (
          <Button
            variant="tertiary"
            size="sm"
            disabled={!hasUnsavedChanges}
            aria-label="Guardar borrador"
            onClick={() => setConfirmationState("save-draft")}
          >
            Guardar Borrador <Icon type="far" name="floppy-disk" />
          </Button>
        )}
        <Button
          variant="default"
          size="sm"
          disabled={faqsStatus !== "draft" && !hasUnsavedChanges}
          aria-label="Publicar"
          onClick={() => {
            setConfirmationState("publish");
          }}
        >
          Publicar <Icon type="fas" name="arrow-up-right" />
        </Button>
      </>,
    );
    return () => {
      setComp(null);
    };
  }, [setComp, hasUnsavedChanges, canCreate, canEdit, faqsStatus]);

  const closeConfirmationModal = () => setConfirmationState("off");

  // open delete-section modal
  const handleDeleteSection = (sect: string) => {
    setSectionToDelete(sect);
    setConfirmationState("delete-section");
  };

  const queryClient = useQueryClient();
  const mutation = useRefreshFaqs();

  const sendData = (payload: DataManagerResult) => {
    mutation.mutate(payload, {
      onSuccess: () => {
        setHasUnsavedChanges(false);
        queryClient.invalidateQueries({
          queryKey: ["faq", { with_drafts: true }],
        });
        queryClient.invalidateQueries({
          queryKey: ["faq-categories"],
        });
      },
      onError: (error) => {
        showToast({
          title: "Ha ocurrido un problema al actualizar",
          description: error?.message,
          options: { duration: 5000, type: "warning" },
        });
      },
    });
  };

  const confirmationModal = useMemo(
    () => (
      <FAQConfirmationModal
        mode={confirmationState !== "off" ? confirmationState : "cancel"}
        isOpen={confirmationState !== "off"}
        onClose={closeConfirmationModal}
        onAccept={() => {
          if (confirmationState === "save-draft") {
            sendData(DataManager(secciones, "draft"));
            showToast({
              title: "Cambios guardados",
              description: "Los cambios se guardaron correctamente.",
              options: {
                duration: 5000,
                customIcon: (
                  <Avatar shape="square" size="xs">
                    <AvatarFallback shape="square" color="action">
                      <Icon type="fas" name="save" />
                    </AvatarFallback>
                  </Avatar>
                ),
              },
            });
          } else if (confirmationState === "publish") {
            sendData(DataManager(secciones, "published"));
            showToast({
              title: "Cambios Publicados",
              description: "Las FAQs fueron publicadas exitosamente.",
              options: { duration: 5000, type: "success" },
            });
          } else if (confirmationState === "delete-section") {
            setSecciones((prev) =>
              prev.filter((sec) => sec.section !== sectionToDelete),
            );
            setHasUnsavedChanges(true);
            showToast({
              title: "Sección eliminada",
              description: `La sección "${sectionToDelete}" fue borrada.`,
              options: { duration: 5000, type: "warning" },
            });
          } else if (confirmationState === "delete-question") {
            if (editingFaq) {
              deleteQuestion(editingFaq.section, editingFaq.index);
              setShowModal(false);
              setEditingFaq(null);
            }
            setHasUnsavedChanges(true);
            showToast({
              title: "Pregunta eliminada",
              description: `La pregunta fue borrada.`,
              options: { duration: 5000, type: "warning" },
            });
          } else {
            showToast({
              title: "Cambios no guardados",
              description: "Los cambios no fueron guardados.",
              options: {
                duration: 5000,
                customIcon: (
                  <Avatar shape="square" size="xs">
                    <AvatarFallback shape="square" color="danger">
                      <Icon type="fas" name="floppy-disk-circle-xmark" />
                    </AvatarFallback>
                  </Avatar>
                ),
              },
            });
            navigate("/portal");
          }
          closeConfirmationModal();
        }}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [confirmationState, sectionToDelete, showToast],
  );

  // CRUD operations
  const addSection = (title: string) => {
    setSecciones((prev) => [...prev, { section: title, questions: [] }]);
    setHasUnsavedChanges(true);
  };
  const renameSection = (oldName: string, newName: string) => {
    setSecciones((prev) =>
      prev.map((sec) =>
        sec.section === oldName ? { ...sec, section: newName } : sec,
      ),
    );
    setHasUnsavedChanges(true);
  };
  const moveSection = (from: number, to: number) => {
    setSecciones((prev) => arrayMove(prev, from, to));
    setHasUnsavedChanges(true);
  };
  const addQuestionBySection = (cat: string, q: Question) => {
    setSecciones((prev) =>
      prev.map((sec) =>
        sec.section === cat
          ? { ...sec, questions: [...sec.questions, q] }
          : sec,
      ),
    );
    setHasUnsavedChanges(true);
  };
  const updateQuestion = (cat: string, idx: number, q: Question) => {
    setSecciones((prev) =>
      prev.map((sec) =>
        sec.section === cat
          ? {
              ...sec,
              questions: sec.questions.map((item, i) => (i === idx ? q : item)),
            }
          : sec,
      ),
    );
    setHasUnsavedChanges(true);
  };
  const deleteQuestion = (cat: string, idx: number) => {
    setSecciones((prev) =>
      prev.map((sec) =>
        sec.section === cat
          ? { ...sec, questions: sec.questions.filter((_, i) => i !== idx) }
          : sec,
      ),
    );
    setHasUnsavedChanges(true);
  };

  const sectionsToOptions = (secs: Section[]) =>
    secs.map((s) => ({
      label: s.section,
      value: s.section,
      showIndicator: true,
    }));

  // Section modal submit
  const validateSection = ([name, mode, initial]: string[]) => {
    if (mode === "create") addSection(name);
    if (mode === "edit") renameSection(initial, name);
    setShowCrudSectionModal(false);
  };

  const handleAddQuestion = (sect?: string) => {
    setEditingFaq(null);
    setCreateCategory(sect ?? "General");
    setShowModal(true);
  };
  const handleEdit = (sect: string, idx: number) => {
    const sec = secciones.find((s) => s.section === sect)!;
    setEditingFaq({ section: sect, index: idx, data: sec.questions[idx] });
    setShowModal(true);
  };
  const handleRenameSection = (sect: string) => {
    setSectionMode("edit");
    setSectionToEdit(sect);
    setShowCrudSectionModal(true);
  };

  // Drag and drop
  const sensors = useSensors(useSensor(PointerSensor));
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setSecciones((prev) =>
      prev.map((sec) => {
        const ids = sec.questions.map((q) => q.id);
        const from = ids.indexOf(active.id as string);
        const to = ids.indexOf(over.id as string);
        if (from === -1 || to === -1) return sec;
        return { ...sec, questions: arrayMove(sec.questions, from, to) };
      }),
    );
    setHasUnsavedChanges(true);
  };

  const truncate = (text: string, max: number = 35): string =>
    text.length > max ? text.slice(0, max - 3) + "..." : text;

  return (
    <>
      {confirmationModal}
      <SectionModal
        faqsData={secciones}
        initialName={sectionMode === "edit" ? sectionToEdit : ""}
        modalType={sectionMode}
        isOpen={showCrudSectionModal}
        onClose={() => setShowCrudSectionModal(false)}
        onSubmit={validateSection}
      />
      <FaqModal
        sectionList={sectionsToOptions(secciones)}
        canDelete={canDelete}
        isOpen={showModal}
        isEditing={!!editingFaq}
        initialQuestion={editingFaq?.data.question}
        initialCategory={editingFaq?.data.category ?? createCategory}
        initialAnswer={editingFaq?.data.answer}
        onClose={() => {
          setShowModal(false);
          setEditingFaq(null);
        }}
        onSubmit={(data) => {
          if (editingFaq) {
            const { section, index } = editingFaq;
            const updated = { ...data, id: editingFaq.data.id };
            if (data.category !== section) {
              deleteQuestion(section, index);
              addQuestionBySection(data.category, updated);
            } else {
              updateQuestion(section, index, updated);
            }
          } else {
            addQuestionBySection(data.category, { ...data, id: getNextId() });
          }
          setShowModal(false);
          setEditingFaq(null);
        }}
        onDelete={() => {
          setConfirmationState("delete-question");
        }}
      />

      <div className="p-4">
        <div className="flex items-end">
          <div className="space-y-1 mr-auto">
            <div className="flex items-center space-x-3">
              <Heading
                variant="h4"
                onClick={() => {
                  console.log(secciones);
                }}
              >
                Preguntas Frecuentes
              </Heading>
              {isLoading ? (
                <Skeleton className="h-7 w-24" />
              ) : listFaqs?.data && listFaqs.data.length > 0 ? (
                listFaqs.data[0].status === "draft" ? (
                  <Badge variant="action">
                    Borrador{" "}
                    <Icon
                      className="text-action-quiet"
                      type="fas"
                      name="edit"
                    />
                  </Badge>
                ) : (
                  <Badge variant="success">
                    Publicado{" "}
                    <Icon className="text-success" type="fas" name="check" />
                  </Badge>
                )
              ) : (
                <Badge variant="action">
                  Borrador{" "}
                  <Icon className="text-action-quiet" type="fas" name="edit" />
                </Badge>
              )}
            </div>
            <p className="text-body">
              Administra las preguntas frecuentes y sus secciones en el portal.
            </p>

            {isLoading ? (
              <Skeleton className="h-6 w-full" />
            ) : (
              <>
                {(listFaqs?.data?.length || 0) > 0 ? (
                  <p className="text-body-quiet font-light">
                    Última actualización:{" "}
                    {listFaqs?.data[0]?.updated_at
                      ? dayjs(listFaqs.data[0].updated_at).format(
                          "MMM DD, YYYY | HH:mm A",
                        )
                      : "-"}{" "}
                    por{" "}
                    <span className="text-action">
                      {`${listFaqs?.data[0]?.user?.name ?? ""} ${listFaqs?.data[0]?.user?.last_name ?? ""}`}
                    </span>
                  </p>
                ) : (
                  <p className="text-body-quiet font-light">
                    No hay actualizaciones
                  </p>
                )}
              </>
            )}
          </div>
          <div className="space-x-2 flex">
            {canEdit && (
              <SortDropdown
                onReorder={(order) => {
                  setSecciones(order);
                  setHasUnsavedChanges(true);
                }}
                sections={secciones}
              />
            )}
            {canCreate && (
              <CreateDropdown
                buttonLabel={
                  <>
                    <span>Añadir</span> <Icon type="fas" name="plus" />
                  </>
                }
                items={[
                  {
                    label: "Nueva sección",
                    faqId: "faq_section",
                    icon: <Icon type="far" name="plus" />,
                    onClick: () => {
                      setSectionMode("create");
                      setShowCrudSectionModal(true);
                    },
                  },
                  {
                    label: "Nueva pregunta",
                    faqId: "faq_question",
                    icon: <Icon type="far" name="plus" />,
                    onClick: () => handleAddQuestion(),
                  },
                ]}
              />
            )}
          </div>
        </div>

        {isLoading ? (
          <FaqsSkeleton />
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            {secciones.map((sec, idx) => (
              <section key={sec.section} className="mt-8">
                <div className="flex space-x-1 mb-2 items-center">
                  {sec.section.length > 35 ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge variant="disabled">
                          {truncate(sec.section)}
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        <p>{sec.section}</p>
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <Badge variant="disabled">{sec.section}</Badge>
                  )}
                  {sec.section !== "General" && (
                    <SectionOptions
                      entity="sección"
                      id={sec.section}
                      onRename={() => handleRenameSection(sec.section)}
                      onMoveUp={() => moveSection(idx, idx - 1)}
                      onMoveDown={() => moveSection(idx, idx + 1)}
                      canMoveUp={idx > 1}
                      canMoveDown={idx < secciones.length - 1}
                      onDelete={() => handleDeleteSection(sec.section)}
                      canDelete={canDelete}
                      canEdit={canEdit}
                    />
                  )}
                  {canCreate && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label="Agregar pregunta"
                          onClick={() => handleAddQuestion(sec.section)}
                        >
                          <Icon type="far" name="plus" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="right">
                        <p>Añadir pregunta</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>

                <SortableContext
                  items={sec.questions.map((q) => q.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {sec.questions.map((q, i) => (
                    <SortableQuestion
                      key={q.id}
                      id={q.id}
                      question={q.question}
                      canEdit={canEdit}
                      onEdit={() => handleEdit(sec.section, i)}
                    />
                  ))}
                </SortableContext>
              </section>
            ))}
          </DndContext>
        )}
        {secciones.length === 1 && secciones[0].questions.length === 0 && (
          <div className="mt-24">
            <FAQEmptyState />
          </div>
        )}
      </div>
    </>
  );
};
